import React, { useState, useEffect } from 'react';
import { apiService, Driver } from '../services/api';

interface UpdateDriverModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpdateDriver: (driver: Driver) => void;
  driver: Driver | null;
}

const UpdateDriverModal: React.FC<UpdateDriverModalProps> = ({ isOpen, onClose, onUpdateDriver, driver }) => {
  const [formData, setFormData] = useState({
    driverName: '',
    email: '',
    mobileNumber: '',
    drivingLicense: '',
    address: '',
    dlFile: null as File | null,
    profilePicture: null as File | null
  });
  const [profilePicturePreview, setProfilePicturePreview] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<{
    driverName?: string;
    email?: string;
    mobileNumber?: string;
    drivingLicense?: string;
    address?: string;
    dlFile?: string;
    profilePicture?: string;
  }>({});

  // Populate form when driver data changes
  useEffect(() => {
    if (driver) {
      // Extract mobile number without +91 prefix for editing
      let mobileNumber = driver.mobileNumber || driver.phoneNumber || '';
      if (mobileNumber.startsWith('+91')) {
        mobileNumber = mobileNumber.substring(3);
      } else if (mobileNumber.startsWith('91')) {
        mobileNumber = mobileNumber.substring(2);
      }
      
      setFormData({
        driverName: driver.driverName || driver.name || '',
        email: driver.email || '',
        mobileNumber: mobileNumber,
        drivingLicense: driver.drivingLicense || driver.licenseNumber || '',
        address: driver.address || '',
        dlFile: null,
        profilePicture: null
      });
      setProfilePicturePreview(driver.profilePicture as string || '');
    }
  }, [driver]);

  // Validation functions (same rules as AddDriverModal)
  const validateDriverName = (name: string): string | undefined => {
    const value = name.trim();
    if (!value) return 'Driver name is required';
    if (value.length < 2) return 'Driver name must be at least 2 characters';
    if (value.length > 50) return 'Driver name must be less than 50 characters';
    if (!/^[a-zA-Z\s]+$/.test(value)) return 'Driver name can only contain letters and spaces';
    return undefined;
  };

  const validateEmail = (email: string): string | undefined => {
    const value = email.trim();
    if (!value) return 'Email is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Please enter a valid email address';
    if (value.length > 100) return 'Email must be less than 100 characters';
    return undefined;
  };

  const validateMobileNumber = (mobile: string): string | undefined => {
    const clean = mobile.replace(/\D/g, '');
    if (!clean) return 'Mobile number is required';
    if (clean.length !== 10) return 'Mobile number must be exactly 10 digits';
    return undefined;
  };

  const validateDrivingLicense = (license: string): string | undefined => {
    const raw = license.trim().toUpperCase();
    if (!raw) return 'Driving license is required';
    const normalized = raw.replace(/[\s-]/g, '');
    const dlPattern = /^[A-Z]{2}[0-9]{2}[0-9]{4}[0-9]{7}$/;
    if (!dlPattern.test(normalized)) {
      return 'Valid formats: DL-14-2023-1234567, DL 14 2023 1234567, or DL1420231234567';
    }
    return undefined;
  };

  const validateAddress = (address: string): string | undefined => {
    const value = address.trim();
    if (!value) return 'Address is required';
    if (value.length < 10) return 'Address must be at least 10 characters';
    if (value.length > 200) return 'Address must be less than 200 characters';
    return undefined;
  };


  const validateProfilePicture = (file: File | null, hasExisting: boolean): string | undefined => {
    // No image required on update; validate only when a new file is provided
    if (!file) return undefined;
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!validTypes.includes(file.type)) return 'Profile picture must be a JPG, JPEG, or PNG file';
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) return 'Profile picture size must be less than 5MB';
    return undefined;
  };

  const validateDLFile = (file: File | null): string | undefined => {
    // Required on update as requested
    // if (!file) return undefined;
    if (!file) return 'Driving license file is required';
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!validTypes.includes(file.type)) return 'Driving license file must be a JPG, JPEG, or PNG file';
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) return 'Driving license file size must be less than 5MB';
    return undefined;
  };

  const validateForm = (): boolean => {
    const errors = {
      driverName: validateDriverName(formData.driverName),
      email: validateEmail(formData.email),
      mobileNumber: validateMobileNumber(formData.mobileNumber),
      drivingLicense: validateDrivingLicense(formData.drivingLicense),
      address: validateAddress(formData.address),
      profilePicture: validateProfilePicture(formData.profilePicture, !!profilePicturePreview),
      dlFile: validateDLFile(formData.dlFile),
    };
    setValidationErrors(errors);
    return !Object.values(errors).some(Boolean);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('UpdateDriverModal - handleSubmit called');
    console.log('UpdateDriverModal - driver:', driver);
    if (!driver?._id) {
      console.log('UpdateDriverModal - No driver ID found');
      return;
    }

    setIsLoading(true);
    setError(null);

    // Validate form before proceeding
    if (!validateForm()) {
      setIsLoading(false);
      return;
    }

    try {
      // Validate mobile number length
      const cleanMobileNumber = formData.mobileNumber.replace(/\D/g, '');
      if (cleanMobileNumber.length !== 10) {
        setError('Please enter a valid 10-digit mobile number');
        setIsLoading(false);
        return;
      }

      // Format mobile number to include +91 prefix
      let formattedMobileNumber = formData.mobileNumber.trim();
      
      // Remove any existing country code or + sign
      formattedMobileNumber = formattedMobileNumber.replace(/^\+91/, '').replace(/^91/, '');
      
      // Remove any non-digit characters
      formattedMobileNumber = formattedMobileNumber.replace(/\D/g, '');
      
      // Add +91 prefix
      formattedMobileNumber = '+91' + formattedMobileNumber;

      const driverData = {
        name: formData.driverName,
        email: formData.email,
        phoneNumber: formattedMobileNumber,
        drivingLicense: formData.drivingLicense.toUpperCase().replace(/[\s-]/g, ''),
        address: formData.address,
        dlFile: formData.dlFile || undefined,
        profilePicture: formData.profilePicture || undefined
      };

      console.log('UpdateDriverModal - Calling API with driver ID:', driver._id);
      console.log('UpdateDriverModal - Driver data to send:', driverData);
      const response = await apiService.updateDriver(driver._id, driverData);
      
      if (response.success && response.data) {
        console.log('UpdateDriverModal - Full response:', response);
        console.log('UpdateDriverModal - Response data:', response.data);
        
        // Handle nested response structure similar to vendor profile
        const updatedDriver = (response.data as any)?.data || response.data;
        console.log('UpdateDriverModal - Updated driver data:', updatedDriver);
        
        onUpdateDriver(updatedDriver);
        onClose();
      } else {
        setError(response.error || 'Failed to update driver');
      }
    } catch (error) {
      setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setFormData({ ...formData, dlFile: file });
    const err = validateDLFile(file);
    setValidationErrors(prev => ({ ...prev, dlFile: err }));
  };

  const handleProfilePictureChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select a valid image file');
        return;
      }
      
      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size should be less than 5MB');
        return;
      }

      setFormData(prev => ({ ...prev, profilePicture: file }));
      const err = validateProfilePicture(file, !!profilePicturePreview);
      setValidationErrors(prev => ({ ...prev, profilePicture: err }));
      
      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfilePicturePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      // Cleared selection; no validation error since image is optional on update
      setFormData(prev => ({ ...prev, profilePicture: null }));
      setValidationErrors(prev => ({ ...prev, profilePicture: undefined }));
    }
  };

  if (!isOpen || !driver) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-4 sm:p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900">Update Driver</h2>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium">{error}</p>
              </div>
            </div>
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Profile Picture Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Profile Picture</label>
            <div className="flex items-center space-x-4">
              <div className="relative">
                {profilePicturePreview ? (
                  <img 
                    src={profilePicturePreview} 
                    alt="Profile Preview" 
                    className="w-16 h-16 rounded-full object-cover border-2 border-gray-200"
                  />
                ) : (
                  <div className="w-16 h-16 bg-gradient-to-r from-jd-blue to-jd-green rounded-full flex items-center justify-center">
                    <span className="text-white font-bold text-lg">
                      {formData.driverName ? formData.driverName.charAt(0).toUpperCase() : 'D'}
                    </span>
                  </div>
                )}
              </div>
              <div className="flex-1">
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png"
                  onChange={handleProfilePictureChange}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-jd-blue file:text-white hover:file:bg-blue-700 file:cursor-pointer cursor-pointer"
                />
                <p className="text-xs text-gray-500 mt-1">
                  JPG, JPEG or PNG. Max size 5MB.
                </p>
                {validationErrors.profilePicture && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.profilePicture}</p>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Driver Name *</label>
              <input
                type="text"
                placeholder="Enter driver name"
                value={formData.driverName}
                onChange={(e) => {
                  const val = e.target.value;
                  setFormData({ ...formData, driverName: val });
                  setValidationErrors(prev => ({ ...prev, driverName: validateDriverName(val) }));
                }}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-jd-green focus:border-transparent ${
                  validationErrors.driverName ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {validationErrors.driverName && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.driverName}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
              <input
                type="email"
                placeholder="Enter email address"
                value={formData.email}
                onChange={(e) => {
                  const val = e.target.value;
                  setFormData({ ...formData, email: val });
                  setValidationErrors(prev => ({ ...prev, email: validateEmail(val) }));
                }}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-jd-green focus:border-transparent ${
                  validationErrors.email ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {validationErrors.email && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.email}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Mobile Number *</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-500 text-sm">+91</span>
                </div>
                <input
                  type="tel"
                  placeholder="Enter 10-digit mobile number"
                  value={formData.mobileNumber}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                    setFormData({ ...formData, mobileNumber: value });
                    setValidationErrors(prev => ({ ...prev, mobileNumber: validateMobileNumber(value) }));
                  }}
                  className={`w-full pl-12 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-jd-green focus:border-transparent ${
                    validationErrors.mobileNumber ? 'border-red-500' : 'border-gray-300'
                  }`}
                  maxLength={10}
                />
              </div>
              {validationErrors.mobileNumber && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.mobileNumber}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                +91 will be automatically added to your number
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Driving License *</label>
              <input
                type="text"
                placeholder="Enter driving license number"
                value={formData.drivingLicense}
                onChange={(e) => {
                  const val = e.target.value.toUpperCase();
                  setFormData({ ...formData, drivingLicense: val });
                  setValidationErrors(prev => ({ ...prev, drivingLicense: validateDrivingLicense(val) }));
                }}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-jd-green focus:border-transparent ${
                  validationErrors.drivingLicense ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {validationErrors.drivingLicense && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.drivingLicense}</p>
              )}
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Address *</label>
            <textarea
              rows={3}
              placeholder="Enter full address"
              value={formData.address}
              onChange={(e) => {
                const val = e.target.value;
                setFormData({ ...formData, address: val });
                setValidationErrors(prev => ({ ...prev, address: validateAddress(val) }));
              }}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-jd-green focus:border-transparent ${
                validationErrors.address ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {validationErrors.address && (
              <p className="mt-1 text-sm text-red-600">{validationErrors.address}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Driving License Document</label>
            <div className="flex space-x-2">
              <button
                type="button"
                className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm"
                onClick={() => document.getElementById('update-dl-file-upload')?.click()}
              >
                Upload
              </button>
              <div className={`flex-1 px-3 py-2 border rounded-lg bg-gray-50 flex items-center ${
                validationErrors.dlFile ? 'border-red-500' : 'border-gray-300'
              }`}>
                <span className="text-gray-500 text-sm">
                  {formData.dlFile ? (formData.dlFile as File).name : 'No file selected'}
                </span>
              </div>
            </div>
            <input
              id="update-dl-file-upload"
              type="file"
              accept=".jpg,.jpeg,.png"
              onChange={handleFileChange}
              className="hidden"
            />
            {validationErrors.dlFile && (
              <p className="mt-1 text-sm text-red-600">{validationErrors.dlFile}</p>
            )}
          </div>
          
          <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors order-2 sm:order-1"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-jd-green text-white rounded-lg hover:bg-green-600 transition-colors order-1 sm:order-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Updating...' : 'Update Driver'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UpdateDriverModal;
